import { useAtomValue } from "jotai";
import { useState } from "react";
import { toast } from "sonner";
import { Lightbox } from "@/components/Lightbox";
import {
  type GalleryItem,
  Thumb,
  UploadCard,
} from "@/modules/plus/photos/components/PhotoGalleryParts";
import type { ModelTemplate } from "@/modules/plus/plus.schema";
import { atoms } from "@/state";

interface CodeLocation {
  code: string;
  contextLabel: string;
  meaning: string;
}

// Enumerate every single-segment code referenced by a template — used to
// build the per-segment gallery rows. Composite-code photos are handled
// separately by scanning the full uploaded map.
const enumerateSegmentCodes = (template: ModelTemplate): CodeLocation[] => {
  const out: CodeLocation[] = [];
  template.fields.forEach((f) => {
    if (!f.codes) {
      return;
    }
    for (const [code, meaning] of Object.entries(f.codes)) {
      out.push({
        code,
        contextLabel: `Field ${f.position} · ${f.name}`,
        meaning,
      });
    }
  });
  for (const a of template.accessories ?? []) {
    out.push({
      code: a.code,
      contextLabel: `Accessory · ${a.category}`,
      meaning: a.meaning,
    });
  }
  return out;
};

// Reads the baseline + uploaded maps directly instead of one byCode atom per
// location — hooks can't be called in a loop, and the location list changes
// with the template.
const useSegmentItems = (locations: CodeLocation[]): GalleryItem[] => {
  const baseline = useAtomValue(atoms.data.accessoryBaseline);
  const uploaded = useAtomValue(atoms.photos.uploadedQuery).data ?? {};
  const items: GalleryItem[] = [];
  for (const loc of locations) {
    const baselineUrl = baseline[loc.code];
    if (baselineUrl) {
      items.push({
        url: baselineUrl,
        code: loc.code,
        meaning: loc.meaning,
        source: "baseline",
        kind: "segment",
      });
    }
    for (const p of uploaded[loc.code] ?? []) {
      items.push({
        url: p.image_url,
        code: loc.code,
        meaning: loc.meaning,
        source: "uploaded",
        uploadedBy: p.uploaded_by,
        imagePath: p.image_path,
        kind: "segment",
      });
    }
  }
  return items;
};

// Scan the full accessory_photos map for composite codes (codes containing
// a dash — i.e. full ordering keys). We attach composites to this gallery
// when their first segment matches one of the template's model codes; codes
// that don't parse to anything still get shown because user data may be wrong.
const useCompositeItems = (template: ModelTemplate): GalleryItem[] => {
  const uploadedQuery = useAtomValue(atoms.photos.uploadedQuery);
  const all = uploadedQuery.data ?? {};
  const modelCodes = new Set(Object.keys(template.fields[0]?.codes ?? {}));

  const out: GalleryItem[] = [];
  for (const [code, photos] of Object.entries(all)) {
    if (!code.includes("-")) {
      continue;
    }
    const firstSegment = code.split("-")[0] ?? "";
    if (!modelCodes.has(firstSegment) && firstSegment !== template.series_id) {
      continue;
    }
    for (const p of photos) {
      out.push({
        url: p.image_url,
        code,
        source: "uploaded",
        uploadedBy: p.uploaded_by,
        imagePath: p.image_path,
        kind: "full",
      });
    }
  }
  return out;
};

// A photo "matches" the current code if EVERY segment of its code is in the
// current selection set.
const buildMatcher = (composedCode: string): ((code: string) => boolean) => {
  const segments = composedCode.split("-").filter(Boolean);
  if (segments.length === 0) {
    return () => false;
  }
  const set = new Set(segments);
  return (code) => {
    const parts = code.split("-").filter(Boolean);
    return parts.length > 0 && parts.every((seg) => set.has(seg));
  };
};

export const TemplatePhotoGallery: React.FC<{
  template: ModelTemplate;
  composedCode: string;
}> = ({ template, composedCode }) => {
  const segmentItems = useSegmentItems(enumerateSegmentCodes(template));
  const compositeItems = useCompositeItems(template);
  const items = [...compositeItems, ...segmentItems];
  const [lightboxItem, setLightboxItem] = useState<GalleryItem | null>(null);
  const removeMutation = useAtomValue(atoms.photos.remove);
  const signedIn = useAtomValue(atoms.auth.isSignedIn);

  const onRemove = async (item: GalleryItem) => {
    if (!item.imagePath) {
      return;
    }
    if (!window.confirm(`Delete this photo for ${item.code}? This can't be undone.`)) {
      return;
    }
    try {
      await removeMutation.mutateAsync({ code: item.code, imagePath: item.imagePath });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Remove failed");
    }
  };

  const matches = buildMatcher(composedCode);
  const matching = items.filter((i) => matches(i.code));
  const matchingKeys = new Set(matching.map((m) => `${m.kind}:${m.code}:${m.url}`));
  const other = items.filter((i) => !matchingKeys.has(`${i.kind}:${i.code}:${i.url}`));
  const hasSelection = composedCode.length > 0;

  const renderGrid = (group: GalleryItem[]) => (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
      {group.map((item, idx) => (
        <Thumb
          // biome-ignore lint/suspicious/noArrayIndexKey: duplicate photos can share code+url; index disambiguates
          key={`${item.kind}-${item.code}-${item.url}-${idx}`}
          item={item}
          signedIn={signedIn}
          busy={removeMutation.isPending}
          onOpen={() => setLightboxItem(item)}
          onRemove={() => onRemove(item)}
        />
      ))}
    </div>
  );

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Photo gallery ({items.length})
        </h2>
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Upload to the code you've built above. Photos whose codes are all currently selected appear
        under <em>Matching</em>; everything else for this template falls under <em>Other</em>.
      </p>

      <div className="mt-4 flex flex-col gap-5">
        <UploadCard
          defaultCode={composedCode}
          helpText="Prefilled from the code builder above — edit if needed."
        />

        {items.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No photos yet. Upload one above to get started.
          </p>
        ) : (
          <>
            {hasSelection && matching.length > 0 && (
              <div className="flex flex-col gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                  Matching the current code ({matching.length})
                </h3>
                {renderGrid(matching)}
              </div>
            )}
            {other.length > 0 && (
              <div className="flex flex-col gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {hasSelection && matching.length > 0
                    ? `Other photos for this template (${other.length})`
                    : `All photos (${other.length})`}
                </h3>
                {renderGrid(other)}
              </div>
            )}
          </>
        )}
      </div>

      <Lightbox
        url={lightboxItem?.url ?? null}
        alt={lightboxItem?.code}
        caption={lightboxItem?.code}
        isOpen={lightboxItem !== null}
        onOpenChange={(open) => {
          if (!open) {
            setLightboxItem(null);
          }
        }}
      />
    </section>
  );
};
